import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StatCard from '../../components/StatCard';
import { feedbackAPI, performanceAPI, reportsAPI, tasksAPI, usersAPI } from '../../services/api';
import { fmtDate, fmtTime } from '../../utils/dateUtils';

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [dailyHours, setDailyHours] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('tasks');
  const [fbForm, setFbForm] = useState({ content: '', rating: '' });
  const [showFbForm, setShowFbForm] = useState(false);

  const load = async () => {
    const params = {};
    if (month) params.month = parseInt(month);
    if (year) params.year = parseInt(year);
    const [u, s, t, d, fb] = await Promise.all([
      usersAPI.get(id),
      performanceAPI.employee(id, params),
      tasksAPI.list({ assigned_to: id }),
      performanceAPI.dailyHours(id, parseInt(month), parseInt(year)),
      feedbackAPI.list({ employee_id: id }),
    ]);
    setEmployee(u.data); setStats(s.data); setTasks(t.data); setDailyHours(d.data); setFeedbacks(fb.data);
  };

  useEffect(() => { load().finally(() => setLoading(false)); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilter = () => { setLoading(true); load().finally(() => setLoading(false)); };

  // Report period: an explicit From/To date range takes priority, otherwise month/year.
  const reportParams = () => (
    fromDate && toDate
      ? { from_date: fromDate, to_date: toDate }
      : { month: parseInt(month), year: parseInt(year) }
  );

  const saveBlob = (data, filename) => {
    const url = URL.createObjectURL(new Blob([data]));
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPerf = async () => {
    const { data } = await reportsAPI.downloadPerformance(id, reportParams());
    saveBlob(data, `work_report_${id}.csv`);
  };

  const downloadMonthly = async () => {
    const { data } = await reportsAPI.downloadMonthly(id, {
      ...reportParams(), tz_offset: -new Date().getTimezoneOffset(),
    });
    saveBlob(data, `timesheet_${id}.csv`);
  };

  const submitFeedback = async (e) => {
    e.preventDefault();
    await feedbackAPI.create({ employee_id: parseInt(id), month: parseInt(month), year: parseInt(year), content: fbForm.content, rating: fbForm.rating ? parseInt(fbForm.rating) : null });
    setShowFbForm(false);
    setFbForm({ content: '', rating: '' });
    const { data } = await feedbackAPI.list({ employee_id: id });
    setFeedbacks(data);
  };

  const statusColor = { pending: 'orange', in_progress: 'blue', completed: 'green', overdue: 'red', cancelled: 'gray' };

  if (loading) return <div className="page-loading">Loading...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2>{employee?.full_name}</h2>
          <p className="text-muted">{employee?.department} · {employee?.email}</p>
        </div>
        <button className="btn btn-ghost" onClick={() => navigate(-1)}>← Back</button>
      </div>

      <div className="filter-bar mb-4">
        <select value={month} onChange={(e) => setMonth(e.target.value)}>
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i + 1} value={i + 1}>{new Date(0, i).toLocaleString('default', { month: 'long' })}</option>
          ))}
        </select>
        <input type="number" placeholder="Year" value={year} onChange={(e) => setYear(e.target.value)} style={{ width: 100 }} />
        <button className="btn btn-ghost" onClick={applyFilter}>Apply</button>
        <span className="text-muted" style={{ fontSize: 12 }}>or date range:</span>
        <input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} title="From date" />
        <input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} title="To date" />
        <button className="btn btn-ghost" onClick={downloadMonthly}>⬇ Timesheet (hours)</button>
        <button className="btn btn-ghost" onClick={downloadPerf}>⬇ Work Report (per task)</button>
        <button className="btn btn-primary" onClick={() => setShowFbForm(!showFbForm)}>
          {showFbForm ? 'Cancel' : '+ Give Feedback'}
        </button>
      </div>

      {showFbForm && (
        <div className="form-card mb-4">
          <h4>Monthly Feedback for {employee?.full_name}</h4>
          <form onSubmit={submitFeedback}>
            <div className="form-row">
              <div className="form-group" style={{ flex: 3 }}>
                <label>Feedback *</label>
                <textarea required rows={3} value={fbForm.content} onChange={(e) => setFbForm({ ...fbForm, content: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Rating (1-5)</label>
                <input type="number" min="1" max="5" value={fbForm.rating} onChange={(e) => setFbForm({ ...fbForm, rating: e.target.value })} />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setShowFbForm(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary">Submit Feedback</button>
            </div>
          </form>
        </div>
      )}

      {stats && (
        <div className="stats-grid">
          <StatCard title="Total Tasks" value={stats.total_tasks} icon="📋" color="blue" />
          <StatCard title="Completed" value={stats.completed} icon="✅" color="green" />
          <StatCard title="Overdue" value={stats.overdue} icon="⏰" color="red" />
          <StatCard title="On-Time" value={stats.on_time_completions} icon="🎯" color="teal" />
          <StatCard title="Avg Hours" value={stats.average_actual_hours != null ? stats.average_actual_hours.toFixed(1) : 'N/A'} icon="⏱" color="purple" />
          <StatCard title="Efficiency" value={stats.efficiency_percent != null ? `${stats.efficiency_percent}%` : 'N/A'} icon="📈" color="orange" />
        </div>
      )}

      {/* Tabs */}
      <div className="filter-bar mt-4">
        {['tasks', 'daily_hours', 'feedback'].map((t) => (
          <button key={t} className={`filter-btn${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>
            {t === 'tasks' ? 'Tasks' : t === 'daily_hours' ? 'Daily Hours' : 'Feedback'}
          </button>
        ))}
      </div>

      {tab === 'tasks' && (
        <div className="card mt-4">
          <div className="card-header"><h3>Assigned Tasks</h3></div>
          <table className="data-table">
            <thead><tr><th>Title</th><th>Status</th><th>Priority</th><th>Created</th><th>Deadline</th><th>Completed</th><th>Actual Hrs</th></tr></thead>
            <tbody>
              {tasks.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-muted">No tasks</td></tr>
              ) : tasks.map((t) => (
                <tr key={t.id}>
                  <td>{t.title}</td>
                  <td><span className={`badge badge-${statusColor[t.status]}`}>{t.status.replace('_', ' ')}</span></td>
                  <td>{t.priority}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(t.created_at)}</td>
                  <td className={t.is_delayed ? 'text-danger' : ''} style={{ whiteSpace: 'nowrap' }}>{t.deadline ? fmtDate(t.deadline) : '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{t.status === 'completed' && t.end_time ? fmtDate(t.end_time) : '—'}</td>
                  <td>{t.actual_hours?.toFixed(1) ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'daily_hours' && (
        <div className="card mt-4">
          <div className="card-header"><h3>Daily Working Hours</h3></div>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Day</th><th>Punch In</th><th>Punch Out</th><th>Total Hours</th></tr></thead>
            <tbody>
              {dailyHours.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-muted">No punch records for this period</td></tr>
              ) : dailyHours.map((d) => (
                <tr key={d.date}>
                  <td>{d.date}</td>
                  <td>{d.day}</td>
                  <td>{fmtTime(d.punch_in)}</td>
                  <td>{fmtTime(d.punch_out)}</td>
                  <td>{d.total_hours?.toFixed(2) ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'feedback' && (
        <div className="card mt-4">
          <div className="card-header"><h3>Monthly Feedback History</h3></div>
          {feedbacks.length === 0 ? (
            <div className="empty-state">No feedback given yet.</div>
          ) : feedbacks.map((fb) => (
            <div key={fb.id} className="issue-item">
              <div className="issue-title">
                {new Date(0, fb.month - 1).toLocaleString('default', { month: 'long' })} {fb.year}
                {fb.rating && <span className="badge badge-blue ml-2">⭐ {fb.rating}/5</span>}
              </div>
              <p style={{ marginTop: 4, fontSize: 13.5 }}>{fb.content}</p>
              {fb.reply && (
                <div style={{ marginTop: 8, padding: 10, background: 'var(--bg-subtle, #f5f6f8)', borderRadius: 8 }}>
                  <div className="text-muted" style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                    Employee Reply{fb.replied_at ? ` · ${fmtDate(fb.replied_at)}` : ''}
                  </div>
                  <p style={{ margin: 0, fontSize: 13 }}>{fb.reply}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
